import { compact, isEmpty, isEqual, isNil, noop, sumBy } from 'lodash';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import type { Fund, Lot, Portfolio, PortfolioNodeDesignationEnum, SimpleFund } from 'venn-api';
import { viewEntity } from 'venn-api';
import { GetColor } from 'venn-ui-kit';
import { assertNotNil, FS, isStrategyNode, logMessageToSentry, useHasFF } from 'venn-utils';
import type { SearchMenuItem } from '../search-menu';
import AllocationPanelRow from './AllocationPanelRow';
import { AllocationPanelSearch } from './AllocationPanelSearch';
import { AllocationPanelSearchRedesigned } from './AllocationPanelSearch.redesign';
import { computeLotsTotalMarketValue, TreeItemUpdateType } from './AllocationUtils';
import { RelativeDropIndicator as DropIndicator } from './drag-and-drop/DropIndicator';
import type { DeprecatedIncorrectlyDefinedItemPercentageProps } from './ItemAllocation';
import { Constants } from './Layout';
import type { AllocationPanelPortfolio } from './types';
import { CASH_BORROWING, CASH_HOLDING } from '../modals/data-uploader/views/review/helpers';
import { type ManualAssignment, useAssignedAllocatorPortfolios } from './hooks/useAssignedAllocatorPortfolios';
import type { StrategyAssignments } from '../modals/data-uploader/hooks/useAssignedPortfolios';
import { convertLotsToApi, type LotInput } from './allocation-search/tax-info';

export type DropPosition = 'NEXT_SIBLING' | 'FIRST_CHILD' | 'LAST_CHILD';

interface AllocationTreeItemProps extends DeprecatedIncorrectlyDefinedItemPercentageProps {
  parentId?: number;
  strategy?: AllocationPanelPortfolio;
  selectedStrategyId: number;
  compareStrategy?: Portfolio;
  allUpdatedFunds?: Map<string, Fund>;
  allOriginalNodes: Map<number, Portfolio>;
  allCompareNodes: Map<number, Portfolio>;
  allGhostChildren: Map<number, Portfolio[]>;
  totalWidth: number;
  hideCompareValue: boolean;
  compareLoading: boolean;
  isRoot?: boolean;
  isInSelectedSubtree?: boolean;
  draggedNode?: Portfolio;
  isInDraggedSubtree?: boolean;
  secondaryTotal?: number;
  isTradesView?: boolean;
  onStrategyUpdate: (updatedStrategy: Portfolio, updateType: TreeItemUpdateType) => void;
  onAddChildFromGhost?: (child: Portfolio) => void;
  onSelectStrategy?: (selectedStrategy?: Portfolio) => void;
  onDrag: (event: React.MouseEvent<HTMLElement>, strategy: Portfolio) => void;
  onDrop: (dropOnNode: Portfolio, positionedAs: DropPosition) => void;
  onPlaceInvestmentInHierarchy?: (investmentNode: Portfolio, hierarchyLevels: string[], constituentId?: number) => void;
  updateFund?: (fundId: string) => Promise<Fund | undefined>;
  hasAccessToCompare?: boolean;
  hideComparisonColumn: boolean;
  rootName: string;
  shouldUseNetAssetsInsteadOfAllocation: boolean;
  findAssignmentForInvestment?: (fundId?: string) => Promise<StrategyAssignments | undefined>;
  assignInvestment?: (fund: Fund, categoryId: string) => ManualAssignment | null;
  hasManualStrategyAssignment?: boolean;
  hasAddInvestmentRedesign?: boolean;
  hasTaxInformation?: boolean;
  isCollapsed: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
}

interface AllocationTreeItemState {
  // Values derived from props, explaining the nature of the item
  isGhost: boolean;
  isStrategy: boolean;
  isSelected: boolean;
  // Values for drag'n'drop
  isDragged: boolean;
  dropIndicator?: DropPosition;
  isAddingNewFund: boolean;
  originalFund?: SimpleFund;
  // Value for toggling manual L4 strategy assignment popover
  isManuallyAssigningL4Strategy: boolean;
}

class AllocationTreeItem extends React.PureComponent<AllocationTreeItemProps, AllocationTreeItemState> {
  static defaultProps = {
    onAddChildFromGhost: noop,
  };

  node = React.createRef<HTMLDivElement>();

  state: AllocationTreeItemState = {
    isGhost: false,
    isStrategy: false,
    isSelected: false,
    isDragged: false,
    dropIndicator: undefined,
    isAddingNewFund: false,
    originalFund: undefined,
    isManuallyAssigningL4Strategy: false,
  };

  static getDerivedStateFromProps(nextProps: AllocationTreeItemProps, prevState: AllocationTreeItemState) {
    const { strategy, compareStrategy, selectedStrategyId, isRoot, draggedNode, hasManualStrategyAssignment } =
      nextProps;
    return {
      isGhost: !strategy && !!compareStrategy,
      isStrategy: strategy ? isStrategyNode(strategy) : isStrategyNode(compareStrategy),
      isSelected: (isRoot && !selectedStrategyId) || (strategy && strategy.id === selectedStrategyId),
      isDragged: draggedNode && strategy && draggedNode.id === strategy.id,
      dropIndicator: prevState.dropIndicator && !draggedNode ? undefined : prevState.dropIndicator,
      isManuallyAssigningL4Strategy: hasManualStrategyAssignment && prevState.isManuallyAssigningL4Strategy,
    };
  }

  componentDidMount() {
    const { isSelected } = this.state;
    if (isSelected && this.node.current && this.node.current.scrollIntoView) {
      if (!this.node.current || !this.node.current.offsetParent) {
        return;
      }
      const isNotFullyVisible =
        this.node.current.offsetTop + this.node.current.offsetHeight > this.node.current.offsetParent.clientHeight;
      if (isNotFullyVisible) {
        this.node.current.scrollIntoView();
      }
    }
  }

  onChildUpdate = (updatedChild: Portfolio, updateType: TreeItemUpdateType) => {
    const { isStrategy } = this.state;
    if (!isStrategy) {
      return;
    }
    const isDeleteFund = updateType === TreeItemUpdateType.DELETE_PORTFOLIO_CHILD;
    // Find the child amongst this strategy's children
    const { strategy, onStrategyUpdate } = this.props;
    if (!strategy) {
      logMessageToSentry('Failed to update portfolio child because of missing strategy');
      return;
    }

    const updatedChildren: Portfolio[] = [...strategy.children];

    const childIdx = strategy.children.findIndex((child: Portfolio) => child.id === updatedChild.id);
    if (childIdx === -1) {
      // If this is a new child, add it to the top (if normal add) or to the bottom (if adding from ghost)
      updateType === TreeItemUpdateType.ADD_FROM_GHOST
        ? updatedChildren.push(updatedChild)
        : updatedChildren.unshift(updatedChild);
    } else {
      // Else replace the child with the updated version or delete the child
      isDeleteFund ? updatedChildren.splice(childIdx, 1) : updatedChildren.splice(childIdx, 1, updatedChild);
    }

    const updatedAllocation = sumBy(updatedChildren, 'allocation');
    const updatedCashBalance = sumBy(updatedChildren, 'cashBalance');
    const updatedNetAssets = sumBy(updatedChildren, 'netAssets');

    const shouldDeleteParent = updatedChildren.length === 0 && FS.has('rcm_portfolio_strategy_enforcement_ff');
    // To avoid cascading the delete to the parent, we would change type
    const newUpdateType = isDeleteFund && !shouldDeleteParent ? TreeItemUpdateType.DELETE_PORTFOLIO : updateType;
    // Send the updated node to the parent component
    onStrategyUpdate(
      {
        ...strategy,
        children: updatedChildren,
        allocation: updatedAllocation,
        cashBalance: updatedCashBalance,
        netAssets: updatedNetAssets,
      },
      newUpdateType,
    );
  };

  onMultiChildAdd = (updatedChilds: Portfolio[]) => {
    const { isStrategy } = this.state;
    if (!isStrategy) {
      return;
    }
    // Find the child amongst this strategy's children
    const { strategy, onStrategyUpdate } = this.props;
    if (!strategy) {
      logMessageToSentry('Failed to update portfolio child because of missing strategy');
      return;
    }

    const updatedChildren = [...updatedChilds, ...strategy.children];

    const updatedAllocation = sumBy(updatedChildren, 'allocation');
    const updatedCashBalance = sumBy(updatedChildren, 'cashBalance');
    const updatedNetAssets = sumBy(updatedChildren, 'netAssets');
    // To avoid cascading the delete to the parent, we would change type
    // Send the updated node to the parent component

    onStrategyUpdate(
      {
        ...strategy,
        children: updatedChildren,
        allocation: updatedAllocation,
        cashBalance: updatedCashBalance,
        netAssets: updatedNetAssets,
      },
      TreeItemUpdateType.ADD_FUND,
    );
  };

  onSetDesignation = (designation: PortfolioNodeDesignationEnum) => {
    const { strategy, onStrategyUpdate } = this.props;
    const { isStrategy, originalFund } = this.state;

    if (isStrategy || !strategy) {
      return;
    }

    const updatedStrategy = {
      ...strategy,
      allocation: 0,
      cashBalance: 0,
      designation,
    };

    if (designation === 'CASH') {
      updatedStrategy.fund = undefined;
      updatedStrategy.cashBalance = strategy?.netAssets ?? 0;
      updatedStrategy.name = updatedStrategy.cashBalance < 0 ? CASH_BORROWING : CASH_HOLDING;
    } else {
      updatedStrategy.fund = originalFund;
      updatedStrategy.allocation = strategy?.netAssets ?? 0;
      updatedStrategy.name = assertNotNil(updatedStrategy.fund?.name);
    }

    onStrategyUpdate?.(updatedStrategy, TreeItemUpdateType.CHANGE_DESIGNATION);
    this.setState({ originalFund: strategy.fund });
  };

  onUpdateLots = (lots: Lot[]) => {
    const { strategy, onStrategyUpdate } = this.props;
    if (!strategy) return;
    const totalMarketValue = computeLotsTotalMarketValue(lots);
    onStrategyUpdate?.(
      { ...strategy, lots, allocation: totalMarketValue, netAssets: totalMarketValue },
      TreeItemUpdateType.CHANGE_LOTS,
    );
  };

  onUpdateAllocation = (updatedAllocation: number) => {
    const { strategy, compareStrategy, onStrategyUpdate, onAddChildFromGhost } = this.props;
    const { isGhost, isStrategy } = this.state;

    if (isStrategy) {
      // We should never be able to update the strategy allocation directly.
      return;
    }

    if (!isGhost) {
      if (!strategy) {
        logMessageToSentry('Failed to update allocation because of missing strategy');
        return;
      }

      const updatedStrategy = {
        ...strategy,
        netAssets: updatedAllocation,
      };

      if (strategy.designation === 'CASH') {
        updatedStrategy.cashBalance = updatedAllocation;
        updatedStrategy.name = updatedAllocation < 0 ? CASH_BORROWING : CASH_HOLDING;
      } else {
        updatedStrategy.allocation = updatedAllocation;
      }

      onStrategyUpdate?.(updatedStrategy, TreeItemUpdateType.CHANGE_ALLOCATION);
      return;
    }

    if (!compareStrategy) {
      logMessageToSentry('Failed to update comparison allocation because of missing compareStrategy');
      return;
    }

    onAddChildFromGhost?.({
      ...compareStrategy,
      allocation: compareStrategy.designation === 'CASH' ? 0 : updatedAllocation,
      cashBalance: compareStrategy.designation === 'CASH' ? updatedAllocation : 0,
      netAssets: updatedAllocation,
      name:
        compareStrategy.designation === 'CASH'
          ? updatedAllocation < 0
            ? CASH_BORROWING
            : CASH_HOLDING
          : compareStrategy.name,
      id: Date.now(),
    });
  };

  onCapitalModifiedInChildren = (updatedStrategy: Portfolio) => {
    const { onStrategyUpdate } = this.props;
    const { isGhost, isStrategy } = this.state;

    if (!isStrategy || isGhost) {
      // We should never perform cash redistribution on ghosts or items without children
      return;
    }

    onStrategyUpdate?.(updatedStrategy, TreeItemUpdateType.EDIT_CAPITAL);
  };

  onDelete = () => {
    const { onStrategyUpdate, strategy, onSelectStrategy, isInSelectedSubtree } = this.props;
    const { isGhost, isSelected } = this.state;
    if (isGhost || (!isInSelectedSubtree && !isSelected)) {
      // We shouldn't be able to delete ghost strategies, or strategies outside of the selected subtree. Do nothing.
      return;
    }
    if (isSelected) {
      onSelectStrategy?.(undefined);
    }
    if (!strategy) {
      logMessageToSentry('Failed to delete the item because of missing strategy');
      return;
    }
    onStrategyUpdate(strategy, TreeItemUpdateType.DELETE_PORTFOLIO_CHILD);
  };

  toggleIsCollapsed = (isCollapsed: boolean) => {
    const { onToggleCollapse } = this.props;
    onToggleCollapse?.(isCollapsed);
  };

  onUpdateStrategyName = (newName: string) => {
    const { strategy } = this.props;
    const node = {
      ...strategy,
      name: newName,
    };
    this.props.onStrategyUpdate(node as Portfolio, TreeItemUpdateType.EDIT_NAME);
  };

  onAddStrategy = (strategy?: Portfolio) => {
    if (strategy) {
      this.onChildUpdate(strategy, TreeItemUpdateType.IMPORT_STRATEGY);
      return;
    }

    const portfolioId = Date.now();

    const node: Partial<Portfolio> = {
      name: '',
      allocation: 0,
      netAssets: 0,
      cashBalance: 0,
      compare: [],
      id: portfolioId,
      children: [],
      draft: true,
    };

    this.onChildUpdate(node as Portfolio, TreeItemUpdateType.ADD_STRATEGY);
  };

  onSelectStrategy = () => {
    const { isGhost, isSelected } = this.state;
    const { strategy, onSelectStrategy } = this.props;
    if (!isGhost && !isSelected) {
      onSelectStrategy?.(strategy);
    }
  };

  onDragMe = (event: React.MouseEvent<HTMLElement>) => {
    const { strategy } = this.props;
    if (!strategy) {
      logMessageToSentry('Failed to drag the item because of missing strategy');
      return;
    }
    this.props.onDrag(event, strategy);
  };

  onToggleDropIndicatorForRow = (showDropIndicator: boolean) => {
    if (!showDropIndicator) {
      this.setState({ dropIndicator: undefined });
      return;
    }

    const { isStrategy } = this.state;
    this.setState({ dropIndicator: isStrategy ? 'FIRST_CHILD' : 'NEXT_SIBLING' });
  };

  handleDrop = () => {
    const { dropIndicator } = this.state;
    if (!dropIndicator) {
      return;
    }
    const { strategy } = this.props;
    if (!strategy) {
      logMessageToSentry('Failed to drop the item because of missing strategy');
      return;
    }

    this.props.onDrop(strategy, dropIndicator);
  };

  onClickAddFund = () => {
    this.setState({ isAddingNewFund: true });
  };

  trackAndUpdateFund = (fundId: string) => {
    this.props.updateFund?.(fundId);
    viewEntity(fundId);
  };

  handleStrategyAssignment = (fund: Fund, strategyId: string, constituentId?: number) => {
    const assignment = this.props.assignInvestment?.(fund, strategyId);

    if (assignment && this.props.onPlaceInvestmentInHierarchy) {
      const investmentNode: Partial<Portfolio> = {
        allocation: 0,
        cashBalance: 0,
        netAssets: 0,
        children: [],
        compare: [],
        id: Date.now(),
        fund,
        name: fund.name,
        periodStart: fund.startRange,
        periodEnd: fund.endRange,
      };

      this.trackAndUpdateFund(fund.id);

      this.props.onPlaceInvestmentInHierarchy(investmentNode as Portfolio, assignment.hierarchyLevels, constituentId);

      if (!this.props.hasTaxInformation) {
        this.setState({
          isAddingNewFund: false,
        });
      }
    }
  };

  handleOnMultiSelectFund = (items: SearchMenuItem[], allocations?: Map<string, number>, lots?: Map<string, Lot[]>) => {
    const funds = compact(items.map((item) => item.value?.fund));
    if (isEmpty(funds)) {
      logMessageToSentry('Failed to select the funds because they are missing');
      return;
    }
    const { strategy } = this.props;

    if (!strategy) {
      logMessageToSentry('Failed to select fund because of missing strategy');
      return;
    }
    const node: Partial<Portfolio>[] = funds.map((fund, index) => {
      const alloc = allocations?.get(fund.id) ?? 0;
      const fundLots = lots?.get(fund.id) ?? [];
      return {
        allocation: alloc,
        cashBalance: 0,
        netAssets: alloc,
        lots: fundLots,
        children: [],
        compare: [],
        id: Date.now() + index,
        fund,
        name: fund.name,
        periodStart: fund.startRange,
        periodEnd: fund.endRange,
      };
    });
    funds.forEach((fund) => fund && this.trackAndUpdateFund(fund.id));

    this.setState({
      isAddingNewFund: false,
    });

    this.onMultiChildAdd(node as Portfolio[]);
  };

  handleOnSingleSelectFund = async (
    item: SearchMenuItem,
    constituentId?: number,
  ): Promise<{ needsManualStrategyAssignment?: boolean } | undefined> => {
    const fund = item.value?.fund;
    if (!fund) {
      logMessageToSentry('Failed to select the fund because it is missing');
      return undefined;
    }
    const { strategy } = this.props;

    if (!strategy) {
      logMessageToSentry('Failed to select fund because of missing strategy');
      return undefined;
    }

    this.trackAndUpdateFund(fund.id);

    const investmentAssignment = await this.props.findAssignmentForInvestment?.(fund.id);

    if (investmentAssignment && investmentAssignment[fund.id]?.assetCategoryId) {
      const categoryId = investmentAssignment[fund.id]!.assetCategoryId;
      const assignment = this.props.assignInvestment?.(fund, categoryId.toString());

      if (assignment && this.props.onPlaceInvestmentInHierarchy) {
        this.setState({
          isAddingNewFund: false,
        });

        const investmentNode: Partial<Portfolio> = {
          allocation: 0,
          cashBalance: 0,
          netAssets: 0,
          children: [],
          compare: [],
          id: Date.now(),
          fund,
          name: fund.name,
          periodStart: fund.startRange,
          periodEnd: fund.endRange,
        };

        this.props.onPlaceInvestmentInHierarchy(investmentNode as Portfolio, assignment.hierarchyLevels, constituentId);
        return undefined;
      }
    }

    if (this.props.hasManualStrategyAssignment && !investmentAssignment) {
      return { needsManualStrategyAssignment: true };
    }

    this.setState({
      isAddingNewFund: false,
    });

    // Feature not enabled or constituent mode - add normally
    const node: Partial<Portfolio> = {
      allocation: 0,
      cashBalance: 0,
      netAssets: 0,
      children: [],
      compare: [],
      id: Date.now(),
      fund,
      name: fund.name,
      periodStart: fund.startRange,
      periodEnd: fund.endRange,
    };

    if (!isNil(constituentId)) {
      this.addInvestmentToConstituent(node as Portfolio, constituentId);
    } else {
      this.onChildUpdate(node as Portfolio, TreeItemUpdateType.ADD_FUND);
    }

    return undefined;
  };

  /**
   * Adds an investment to a specific portfolio constituent
   * TODO(VENN-30330): this needs to be updated to place be placed into the correct proper hierearchy
   * (and L0-L4 nodes will need to be created and mapped automatically if available)
   */
  addInvestmentToConstituent = (investment: Portfolio, constituentId: number) => {
    const { strategy, onStrategyUpdate } = this.props;
    if (!strategy) {
      logMessageToSentry('Failed to add investment to constituent because of missing strategy');
      return;
    }

    // Recursively find and update the constituent at any depth in the tree
    const updateConstituentInTree = (node: Portfolio): Portfolio | null => {
      if (node.id === constituentId) {
        return {
          ...node,
          children: [investment, ...(node.children || [])],
        };
      }

      const children = node.children || [];
      let updated = false;
      const updatedChildren = children.map((child) => {
        const updatedChild = updateConstituentInTree(child);
        if (updatedChild) {
          updated = true;
          return updatedChild;
        }
        return child;
      });

      return updated ? { ...node, children: updatedChildren } : null;
    };

    const updatedStrategy = updateConstituentInTree(strategy);
    if (!updatedStrategy) {
      logMessageToSentry(`Failed to find constituent with id ${constituentId}`);
      return;
    }

    onStrategyUpdate(updatedStrategy, TreeItemUpdateType.ADD_FUND);
  };

  updateDescendantLots = (descendantFundId: string, lots: LotInput[]) => {
    const { strategy, onStrategyUpdate } = this.props;
    if (!strategy) {
      logMessageToSentry('Failed to update lots because of missing strategy');
      return;
    }

    const findAndUpdateDescendant = (node: Portfolio): Portfolio | null => {
      if (node.fund?.id === descendantFundId) {
        const totalMarketValue = lots.reduce((sum, lot) => sum + lot.marketValue, 0);
        return {
          ...node,
          lots: convertLotsToApi(lots),
          allocation: totalMarketValue,
          netAssets: totalMarketValue,
        };
      }
      const children: Portfolio[] = node.children || [];
      let updated = false;
      const updatedChildren = children.map((child) => {
        const updatedChild = findAndUpdateDescendant(child);
        if (updatedChild) {
          updated = true;
          return updatedChild;
        }
        return child;
      });

      return updated ? { ...node, children: updatedChildren } : null;
    };

    const updatedStrategy = findAndUpdateDescendant(strategy);
    if (!updatedStrategy) {
      logMessageToSentry(`Failed to update lots for fund with id ${descendantFundId}`);
      return;
    }
    onStrategyUpdate(updatedStrategy, TreeItemUpdateType.CHANGE_LOTS);
  };

  onCancelAddingNewFund = () => {
    this.setState({
      isAddingNewFund: false,
    });
  };

  render() {
    const {
      strategy,
      compareStrategy,
      allUpdatedFunds,
      allOriginalNodes,
      hideCompareValue,
      compareLoading,
      isRoot,
      onSelectStrategy,
      isInSelectedSubtree,
      totalWidth,
      draggedNode,
      isInDraggedSubtree,
      isPercentageMode,
      baseAllocation,
      orignalBaseAllocation,
      secondaryTotal,
      isTradesView,
      hideComparisonColumn,
      rootName,
      shouldUseNetAssetsInsteadOfAllocation,
      hasAddInvestmentRedesign,
      hasTaxInformation,
      isCollapsed,
    } = this.props;
    if (!strategy && !compareStrategy) {
      return undefined;
    }

    const { isGhost, isStrategy, isSelected, isDragged, dropIndicator, isAddingNewFund } = this.state;

    const originalNode = isGhost
      ? undefined
      : allOriginalNodes.has(strategy!.id)
        ? allOriginalNodes.get(strategy!.id)
        : undefined;

    const isLotsModified = !isGhost && !isEqual(strategy?.lots ?? [], originalNode?.lots ?? []);

    const qaClass = {
      itemType: isRoot ? 'qa-portfolio-wrapper' : isStrategy ? 'qa-strategy' : 'qa-investment',
      isRootPortfolio: isRoot ? 'qa-root-portfolio' : undefined,
    };

    return (
      <Item className={qaClass.itemType} ref={this.node} onMouseUp={this.handleDrop}>
        <AllocationPanelRow
          className={qaClass.isRootPortfolio}
          strategy={strategy}
          compareStrategy={compareStrategy}
          originalNode={originalNode}
          isRoot={!!isRoot}
          isGhost={isGhost}
          isStrategy={isStrategy}
          isSelected={isSelected}
          isDragged={isDragged}
          isInDraggedSubtree={!!isInDraggedSubtree}
          isDraggingInProgress={!!draggedNode}
          totalWidth={totalWidth}
          isInSelectedSubtree={!!isInSelectedSubtree}
          hideCompareValue={hideCompareValue}
          compareLoading={compareLoading}
          isCollapsed={isCollapsed}
          toggleIsCollapsed={this.toggleIsCollapsed}
          onClickAddFund={this.onClickAddFund}
          onUpdateStrategyName={this.onUpdateStrategyName}
          onDelete={this.onDelete}
          onSelectStrategy={onSelectStrategy !== undefined ? this.onSelectStrategy : undefined}
          onUpdateAllocation={this.onUpdateAllocation}
          onSetDesignation={this.onSetDesignation}
          onUpdateLots={this.onUpdateLots}
          isLotsModified={isLotsModified}
          onAddStrategy={this.onAddStrategy}
          onDrag={this.onDragMe}
          onToggleDropIndicatorForRow={this.onToggleDropIndicatorForRow}
          isPercentageMode={isPercentageMode}
          baseAllocation={baseAllocation}
          orignalBaseAllocation={orignalBaseAllocation}
          secondaryTotal={secondaryTotal}
          isTradesView={isTradesView}
          updatedFund={
            strategy && strategy.fund && !isStrategy && allUpdatedFunds
              ? allUpdatedFunds.get(strategy.fund.id)
              : undefined
          }
          hasAccessToCompare={this.props.hasAccessToCompare}
          onUpdateChildrenAllocations={this.onCapitalModifiedInChildren}
          hideComparisonColumn={hideComparisonColumn}
          rootName={rootName}
          dataTestId={qaClass.isRootPortfolio}
          shouldUseNetAssetsInsteadOfAllocation={shouldUseNetAssetsInsteadOfAllocation}
        />

        {(dropIndicator === 'NEXT_SIBLING' || dropIndicator === 'FIRST_CHILD') && (
          <DropIndicator verticalPadding={Constants.NAME_MARGIN} />
        )}

        {/* Portfolio Allocator - Add investment flow redesigned + taxes */}
        {hasAddInvestmentRedesign && isAddingNewFund && isStrategy && !isGhost && (
          <AllocationPanelSearchRedesigned
            onCancel={this.onCancelAddingNewFund}
            onMultiSelectFund={this.handleOnMultiSelectFund}
            onSingleSelectFund={this.handleOnSingleSelectFund}
            onStrategyAssignment={this.handleStrategyAssignment}
            onAddTaxInformation={this.updateDescendantLots}
            strategy={strategy!}
            nodeRef={this.node}
            hasTaxInformation={hasTaxInformation}
          />
        )}

        {!hasAddInvestmentRedesign && isAddingNewFund && isStrategy && !isGhost && (
          <AllocationPanelSearch
            onCancel={this.onCancelAddingNewFund}
            onMultiSelectFund={this.handleOnMultiSelectFund}
            onSingleSelectFund={this.handleOnSingleSelectFund}
            onStrategyAssignment={this.handleStrategyAssignment}
            strategy={strategy!}
            nodeRef={this.node}
          />
        )}
      </Item>
    );
  }
}

/**
 * This can be removed when this component is rewritten to a functional component, and hooks can be used directly
 *
 * @param Component
 */
const withHook = (Component: typeof AllocationTreeItem) => {
  const WrappedComponent = React.memo((props: AllocationTreeItemProps) => {
    const hasAddInvestmentRedesign = useHasFF('add_investment_redesign_ff');
    const hasTaxImpactFF = useHasFF('tax_impact_ff');
    const { assignInvestment, findInvestmentAssignment, hasManualStrategyAssignment } =
      useAssignedAllocatorPortfolios();

    const hasTaxInformation = useMemo(() => {
      if (!hasTaxImpactFF) return false;
      for (const node of props.allOriginalNodes.values()) {
        if (node.lots && node.lots.length > 0) return true;
      }
      return false;
    }, [hasTaxImpactFF, props.allOriginalNodes]);

    return (
      <Component
        {...props}
        hasAddInvestmentRedesign={hasAddInvestmentRedesign}
        hasTaxInformation={hasTaxInformation}
        findAssignmentForInvestment={findInvestmentAssignment}
        hasManualStrategyAssignment={hasManualStrategyAssignment}
        assignInvestment={assignInvestment}
      />
    );
  });
  WrappedComponent.displayName = 'AllocationTreeItem';
  return WrappedComponent;
};

export default withHook(AllocationTreeItem);

const Item = styled.div`
  position: relative;
`;

export const BaseIndentation = styled.div`
  min-width: ${Constants.NAME_MARGIN}px;
  width: ${Constants.NAME_MARGIN}px;
  position: absolute;
`;

export const ItemChildren = styled.div<{ isGhost: boolean }>`
  border-left: dashed 1px ${({ isGhost }) => (isGhost ? GetColor.GreyScale.Grey40 : GetColor.GreyScale.Grey60)};
  margin-left: ${Constants.NAME_MARGIN}px;
`;
